import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import { NETZ_FEHLER_TEXT, istNetzFehler } from '../netzFehler';
import { istTalentStatus } from './talentpoolLogik';
import {
    TALENTPOOL_ABLAGE,
    TALENTPOOL_SPALTEN,
    type TalentDatei,
    type TalentStatus,
    type TalentpoolZeile,
} from './talentpoolTypes';

/**
 * Datenbankzugriff des Talentpools (docs/talentpool-plan.md): liest
 * `public.talentpool` (Nr. 188, RLS: eigene Firma und Recht
 * `talentpool.verwalten`) und schreibt ausschließlich über die RPCs aus
 * Nr. 189. Anhänge liegen in der privaten Ablage `talentpool-dateien` und
 * werden nur über kurz gültige signierte Links geöffnet.
 *
 * LESEN UND SCHREIBEN WERFEN `TalentpoolFehler` mit deutschem Satz; fehlt das
 * SQL in dieser Umgebung, kommt der Code `nicht_eingerichtet` — die Ansicht
 * zeigt dann einen Hinweis statt eines Absturzes. Nur `zaehleNeu` (Abzeichen
 * im Menü) gibt bei JEDEM Fehler null.
 *
 * `supabase` ist im Bestand untypisiert; jede Antwort wird deshalb als
 * `unknown` gelesen und von Hand geprüft.
 */

const T_TALENTPOOL = 'talentpool';
const SPALTEN = TALENTPOOL_SPALTEN.join(', ');

/** Höchstens so viele Bewerbungen lädt die Ansicht (neueste zuerst). */
export const TALENTPOOL_GRENZE = 2000;
/** PostgREST liefert je Anfrage höchstens 1000 Zeilen und sagt es nicht (seitenweise.ts). */
const SEITE = 1000;
/** Gültigkeit eines Anhang-Links in Sekunden. */
export const DATEI_LINK_SEKUNDEN = 120;

// ─── Fehler ─────────────────────────────────────────────────────────────────

export type TalentpoolFehlerCode = 'nicht_eingerichtet' | 'kein_recht' | 'nicht_gefunden' | 'ungueltig' | 'netz' | 'unbekannt';

export class TalentpoolFehler extends Error {
    readonly code: TalentpoolFehlerCode;
    constructor(code: TalentpoolFehlerCode, text: string) {
        super(text);
        this.name = 'TalentpoolFehler';
        this.code = code;
    }
}

export const TALENTPOOL_NICHT_EINGERICHTET_TEXT =
    'Talentpool ist auf dem Server noch nicht eingerichtet (Migration Nr. 188/189 fehlt).';
export const TALENTPOOL_KEIN_RECHT_TEXT = 'Kein Recht für den Talentpool.';

const ERSATZ_TEXT: Record<TalentpoolFehlerCode, string> = {
    nicht_eingerichtet: TALENTPOOL_NICHT_EINGERICHTET_TEXT,
    kein_recht: TALENTPOOL_KEIN_RECHT_TEXT,
    nicht_gefunden: 'Diese Bewerbung gibt es nicht mehr — bitte die Liste neu laden.',
    ungueltig: 'Dieser Status ist unbekannt — bitte die Liste neu laden.',
    netz: NETZ_FEHLER_TEXT,
    unbekannt: 'Der Vorgang ist fehlgeschlagen.',
};

const feld = (o: unknown, schluessel: string): unknown =>
    o !== null && typeof o === 'object' ? (o as Record<string, unknown>)[schluessel] : undefined;

const fehlerCode = (e: unknown): string => {
    const c = feld(e, 'code');
    return typeof c === 'string' ? c : typeof c === 'number' ? String(c) : '';
};

const fehlerText = (e: unknown): string => {
    const m = feld(e, 'message');
    return typeof m === 'string' ? m.trim() : '';
};

/** SQL Nr. 188/189 fehlt: Tabelle (42P01/PGRST205), Funktion (PGRST202/42883) oder HTTP 404. */
export const istNichtEingerichtet = (e: unknown, httpStatus?: number): boolean => {
    const code = fehlerCode(e);
    if (code === '42P01' || code === 'PGRST205' || code === 'PGRST202' || code === '42883') return true;
    if (httpStatus === 404 || feld(e, 'status') === 404) return true;
    return /could not find the (function|table)/i.test(fehlerText(e));
};

/**
 * Irgendein Fehler → TalentpoolFehler. Die RPCs aus Nr. 189 melden 42501
 * (kein Recht), P0002 (nicht gefunden) und 22023 (Status ungültig); ihre
 * Meldungstexte sind Schlüsselwörter (`kein_recht` …), deshalb stehen hier
 * eigene Sätze.
 */
export const alsTalentpoolFehler = (e: unknown, httpStatus?: number, kontext = ERSATZ_TEXT.unbekannt): TalentpoolFehler => {
    if (e instanceof TalentpoolFehler) return e;
    const code = fehlerCode(e);
    if (code === '42501') return new TalentpoolFehler('kein_recht', ERSATZ_TEXT.kein_recht);
    if (code === 'P0002') return new TalentpoolFehler('nicht_gefunden', ERSATZ_TEXT.nicht_gefunden);
    if (code === '22023') return new TalentpoolFehler('ungueltig', ERSATZ_TEXT.ungueltig);
    if (httpStatus === 0 || istNetzFehler(e)) return new TalentpoolFehler('netz', ERSATZ_TEXT.netz);
    if (istNichtEingerichtet(e, httpStatus)) return new TalentpoolFehler('nicht_eingerichtet', ERSATZ_TEXT.nicht_eingerichtet);
    return new TalentpoolFehler('unbekannt', `${kontext} (${describeError(e)})`);
};

/** Antwort einer Abfrage/RPC prüfen: `data` zurück, jeder Fehler als TalentpoolFehler. */
const datenOderFehler = (antwort: unknown, kontext: string): unknown => {
    const error = feld(antwort, 'error');
    if (error) {
        const status = feld(antwort, 'status');
        throw alsTalentpoolFehler(error, typeof status === 'number' ? status : undefined, kontext);
    }
    return feld(antwort, 'data') ?? null;
};

const rufe = async (name: string, params: Record<string, unknown>, kontext: string): Promise<unknown> => {
    let antwort: unknown;
    try {
        antwort = await supabase.rpc(name, params);
    } catch (e) {
        throw alsTalentpoolFehler(e, undefined, kontext);
    }
    return datenOderFehler(antwort, kontext);
};

// ─── Zeilen lesen ───────────────────────────────────────────────────────────

type Roh = Record<string, unknown>;

/** jsonb kommt als Objekt; ein JSON-Text (älterer Treiber, Proxy) wird gelesen. */
const alsObjekt = (v: unknown): Roh | null => {
    let w = v;
    if (typeof w === 'string') {
        try {
            w = JSON.parse(w);
        } catch {
            return null;
        }
    }
    return w !== null && typeof w === 'object' && !Array.isArray(w) ? (w as Roh) : null;
};

const alsListe = (v: unknown): unknown[] => {
    let w = v;
    if (typeof w === 'string') {
        try {
            w = JSON.parse(w);
        } catch {
            return [];
        }
    }
    return Array.isArray(w) ? w : [];
};

const textOderNull = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);

const zahlOderNull = (v: unknown): number | null => {
    const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : Number.NaN;
    return Number.isFinite(n) ? n : null;
};

const texte = (v: unknown): string[] =>
    alsListe(v).filter((x): x is string => typeof x === 'string' && x.trim() !== '');

/** Ein Eintrag aus `dateien`; ohne Pfad unbrauchbar → null. */
const dateiAus = (roh: unknown): TalentDatei | null => {
    const d = alsObjekt(roh);
    if (!d) return null;
    const pfad = textOderNull(d.pfad);
    if (!pfad) return null;
    const nr = zahlOderNull(d.nr);
    return {
        nr: nr ?? 0,
        pfad,
        name: textOderNull(d.name) ?? pfad.split('/').pop() ?? pfad,
        typ: textOderNull(d.typ) ?? '',
        groesse: zahlOderNull(d.groesse) ?? 0,
    };
};

/**
 * Zeile `talentpool` (Tabelle oder RPC-Antwort) → TalentpoolZeile; ohne id,
 * Name oder Eingang unlesbar → null. Ein unbekannter Status (gibt der CHECK
 * nicht her) gilt als `neu`, damit die Bewerbung sichtbar bleibt.
 */
export const talentAusZeile = (roh: unknown): TalentpoolZeile | null => {
    const z = alsObjekt(roh);
    if (!z) return null;
    const id = textOderNull(z.id);
    const name = textOderNull(z.name);
    const eingegangenAm = textOderNull(z.eingegangen_am);
    if (!id || !name || !eingegangenAm) return null;
    const lat = zahlOderNull(z.lat);
    const lng = zahlOderNull(z.lng);
    const mitStandort = lat !== null && lng !== null;
    const dateien = alsListe(z.dateien)
        .map(dateiAus)
        .filter((d): d is TalentDatei => d !== null)
        .sort((a, b) => a.nr - b.nr);
    return {
        id,
        company_id: textOderNull(z.company_id) ?? '',
        website_id: textOderNull(z.website_id) ?? '',
        eingegangen_am: eingegangenAm,
        created_at: textOderNull(z.created_at) ?? eingegangenAm,
        updated_at: textOderNull(z.updated_at) ?? eingegangenAm,
        rolle: textOderNull(z.rolle) ?? 'unbekannt',
        arbeitsform: texte(z.arbeitsform),
        lat: mitStandort ? lat : null,
        lng: mitStandort ? lng : null,
        plz_ort: textOderNull(z.plz_ort),
        ort_naehe: textOderNull(z.ort_naehe),
        umkreis_km: zahlOderNull(z.umkreis_km),
        bundesweit: z.bundesweit === true,
        qualifikation: texte(z.qualifikation),
        qualifikation_sonstiges: textOderNull(z.qualifikation_sonstiges),
        abschluss: textOderNull(z.abschluss),
        erfahrung_jahre: zahlOderNull(z.erfahrung_jahre),
        verfuegbar_ab: textOderNull(z.verfuegbar_ab),
        name,
        email: textOderNull(z.email),
        telefon: textOderNull(z.telefon),
        nachricht: textOderNull(z.nachricht),
        herkunft: textOderNull(z.herkunft),
        quelle_seite: textOderNull(z.quelle_seite),
        kampagne: textOderNull(z.kampagne),
        einwilligung_talentpool: z.einwilligung_talentpool === true,
        einwilligung_datenschutz: z.einwilligung_datenschutz === true,
        angaben: alsObjekt(z.angaben) ?? {},
        dateien,
        dateien_erwartet: Math.max(0, Math.trunc(zahlOderNull(z.dateien_erwartet) ?? 0)),
        status: istTalentStatus(z.status) ? z.status : 'neu',
        notiz: textOderNull(z.notiz),
        bearbeitet_von: textOderNull(z.bearbeitet_von),
        bearbeitet_am: textOderNull(z.bearbeitet_am),
        abgeschlossen_am: textOderNull(z.abgeschlossen_am),
        loeschen_ab: textOderNull(z.loeschen_ab),
    };
};

// ─── Lesen ──────────────────────────────────────────────────────────────────

export interface TalentpoolStand {
    /** Neueste zuerst, höchstens TALENTPOOL_GRENZE. */
    zeilen: TalentpoolZeile[];
    /** Es gibt vermutlich ältere Bewerbungen, als geladen wurden. */
    gekappt: boolean;
}

/**
 * Die neuesten `TALENTPOOL_GRENZE` Bewerbungen der Firma (RLS), in Seiten zu
 * 1000 — ein `limit(2000)` allein lieferte still nur 1000 (Max rows von
 * PostgREST). Sortierung eindeutig (Eingang, dann id), Doppelte zwischen zwei
 * Seiten fallen weg.
 */
export const ladeTalentpool = async (): Promise<TalentpoolStand> => {
    const kontext = 'Talentpool nicht geladen.';
    const roh: unknown[] = [];
    for (let von = 0; von < TALENTPOOL_GRENZE; von += SEITE) {
        const bis = Math.min(von + SEITE, TALENTPOOL_GRENZE) - 1;
        let antwort: unknown;
        try {
            antwort = await supabase.from(T_TALENTPOOL).select(SPALTEN)
                .order('eingegangen_am', { ascending: false })
                .order('id', { ascending: true })
                .range(von, bis);
        } catch (e) {
            throw alsTalentpoolFehler(e, undefined, kontext);
        }
        const data = datenOderFehler(antwort, kontext);
        const seite = Array.isArray(data) ? data : [];
        roh.push(...seite);
        if (seite.length < bis - von + 1) break;
    }
    const gesehen = new Set<string>();
    const zeilen: TalentpoolZeile[] = [];
    for (const r of roh) {
        const z = talentAusZeile(r);
        if (!z || gesehen.has(z.id)) continue;
        gesehen.add(z.id);
        zeilen.push(z);
    }
    return { zeilen, gekappt: roh.length >= TALENTPOOL_GRENZE };
};

/**
 * Zahl der Bewerbungen mit Status `neu` für das Abzeichen im Menü —
 * head-only, es kommt keine Zeile über die Leitung. null bei JEDEM Fehler,
 * auch bei fehlender Tabelle: das Abzeichen entfällt, die App läuft weiter.
 */
export async function zaehleNeu(): Promise<number | null> {
    try {
        const antwort: unknown = await supabase
            .from(T_TALENTPOOL)
            .select('id', { count: 'exact', head: true })
            .eq('status', 'neu');
        if (feld(antwort, 'error')) return null;
        const count = feld(antwort, 'count');
        return typeof count === 'number' && Number.isFinite(count) ? count : null;
    } catch {
        return null;
    }
}

// ─── Schreiben (nur RPCs, Nr. 189) ──────────────────────────────────────────

/** Status und Notiz setzen. Rückgabe: die gespeicherte Zeile. */
export const bearbeiteTalent = async (id: string, status: TalentStatus, notiz: string): Promise<TalentpoolZeile> => {
    const data = await rufe('talentpool_bearbeiten', { p_id: id, p_status: status, p_notiz: notiz }, 'Nicht gespeichert.');
    const zeile = talentAusZeile(Array.isArray(data) ? data[0] : data);
    if (!zeile) {
        throw new TalentpoolFehler('unbekannt', 'Gespeichert, aber die Antwort der Datenbank ist unlesbar — bitte neu laden.');
    }
    return zeile;
};

export interface LoeschErgebnis {
    /** Anhänge, die mit der Zeile gingen. */
    dateien: number;
    /** Klartext, falls Anhänge in der Ablage liegen blieben — die Zeile ist trotzdem weg. */
    dateienFehler: string | null;
}

const pfadeAus = (data: unknown): string[] => texte(feld(alsObjekt(data), 'pfade'));

/**
 * Bewerbung löschen: erst die Zeile (RPC, prüft Recht und Firma), dann die
 * Anhänge, deren Pfade die RPC zurückgibt. Scheitert das Entfernen der
 * Anhänge, ist die Zeile trotzdem weg — der Fehler kommt als Klartext zurück,
 * nicht als Wurf, damit die Liste die Zeile entfernt.
 */
export const loescheTalent = async (id: string): Promise<LoeschErgebnis> => {
    const pfade = pfadeAus(await rufe('talentpool_loeschen', { p_id: id }, 'Nicht gelöscht.'));
    if (pfade.length === 0) return { dateien: 0, dateienFehler: null };
    const anhaenge = pfade.length === 1 ? 'ein Anhang' : `${pfade.length} Anhänge`;
    const satz = (grund: string) =>
        `Die Bewerbung ist gelöscht, ${anhaenge} aber nicht (${grund}). Bitte einem Admin melden.`;
    try {
        const antwort: unknown = await supabase.storage.from(TALENTPOOL_ABLAGE).remove(pfade);
        const error = feld(antwort, 'error');
        if (error) return { dateien: pfade.length, dateienFehler: satz(describeError(error)) };
    } catch (e) {
        return { dateien: pfade.length, dateienFehler: satz(istNetzFehler(e) ? NETZ_FEHLER_TEXT : describeError(e)) };
    }
    return { dateien: pfade.length, dateienFehler: null };
};

/** Fehler der Storage-API → TalentpoolFehler (sie kennt keine Postgres-Codes). */
const alsDateiFehler = (e: unknown): TalentpoolFehler => {
    const text = fehlerText(e);
    const status = String(feld(e, 'statusCode') ?? feld(e, 'status') ?? '');
    if (/bucket not found/i.test(text)) return new TalentpoolFehler('nicht_eingerichtet', ERSATZ_TEXT.nicht_eingerichtet);
    if (status === '404' || /not found/i.test(text)) {
        return new TalentpoolFehler('nicht_gefunden', 'Anhang nicht gefunden — vielleicht noch nicht übertragen oder schon gelöscht.');
    }
    if (status === '403' || /row-level security|unauthorized|not authorized/i.test(text)) {
        return new TalentpoolFehler('kein_recht', ERSATZ_TEXT.kein_recht);
    }
    if (istNetzFehler(e)) return new TalentpoolFehler('netz', ERSATZ_TEXT.netz);
    return new TalentpoolFehler('unbekannt', `Anhang nicht geöffnet (${describeError(e)}).`);
};

/** Kurz gültiger Link (DATEI_LINK_SEKUNDEN) auf einen Anhang in der privaten Ablage. */
export const dateiLink = async (pfad: string): Promise<string> => {
    let antwort: unknown;
    try {
        antwort = await supabase.storage.from(TALENTPOOL_ABLAGE).createSignedUrl(pfad, DATEI_LINK_SEKUNDEN);
    } catch (e) {
        throw alsDateiFehler(e);
    }
    const error = feld(antwort, 'error');
    if (error) throw alsDateiFehler(error);
    const url = feld(feld(antwort, 'data'), 'signedUrl');
    if (typeof url !== 'string' || url === '') {
        throw new TalentpoolFehler('unbekannt', 'Anhang nicht geöffnet: die Ablage hat keinen Link geliefert.');
    }
    return url;
};
